'use client';

import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useCart } from '@/contexts/CartContext';
import { useProduct } from '@/contexts/ProductContext';
import { ADD_TO_CART_GENERIC } from '@/lib/cart/cartErrorMessages';
import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import CTAButton from '@/components/elements/CTAButton';

const AddToCartButton = ({ block, quantity, className = 'py-4 font-sans text-lg font-normal', radius }) => {
  const { productData, selectedVariant, additionalProducts } = useProduct();
  const { addCartItems } = useCart();
  const product = productData.shopifyProduct;
  const productUrl = productData.url;
  const router = useRouter();
  const [isAddToCartLoading, setIsAddToCartLoading] = useState(false);
  const [cartError, setCartError] = useState(null);

  const itemsToAdd = useMemo(() => {
    if (!selectedVariant || !product) {
      return [];
    }

    const items = [
      {
        variant: selectedVariant,
        product,
        quantity:
          (quantity ?? block?.quantity ?? block?.default_quantity) || 1,
        customAttributes: [
          { key: '_productType', value: product.productType },
          ...(productUrl ? [{ key: '_productUrl', value: productUrl }] : []),
        ],
      },
      ...additionalProducts.map((item) => {
        // Here we set up a variant-like object to pass to the cart.
        // The cart wants shopify variant objects but the "type" of additional
        // products are more like line items. The most notable difference is that
        // the variant title is in a different place and it has custom attributes.
        const almostVariant = {
          ...item,
          productTitle: item.title,
          title: item.variantTitle,
        };
        delete almostVariant.variantTitle;

        return {
          variant: almostVariant,
          quantity: 1,
          customAttributes: item.customAttributes ?? [],

          // The product field is inaccurate here because we're passing the main
          // product being added to cart, not the additional product. But, as it
          // is simply read in by the optimistic cart, it makes no issues with
          // our setup. Still, for clarity we want to refactor this and the
          // above "almostVariant" because it'll lead to confusion later.
          product,
        };
      }),
    ];

    return items;
  }, [block?.default_quantity, block?.quantity, product, productUrl, quantity, selectedVariant, additionalProducts]);

  if (!selectedVariant || !product) return null;

  const addToCartHandle = async (e) => {
    e.preventDefault();
    if (isAddToCartLoading) return;
    setIsAddToCartLoading(true);
    setCartError(null);

    try {
      // Generate a shared split ID at the moment of action to ensure
      // stable memoization and consistent grouping in the cart.
      const splitId = crypto.randomUUID();
      const finalItems = itemsToAdd.map((item) => ({
        ...item,
        customAttributes: [
          ...(item.customAttributes ?? []),
          { key: "_split_id", value: splitId },
        ],
      }));

      const { error } = await addCartItems(finalItems);

      if (error) {
        console.error(error.message);
        setCartError(error.message || ADD_TO_CART_GENERIC);
        return;
      }

      router.push('/cart');
    } catch (error) {
      console.error(error);
      setCartError(ADD_TO_CART_GENERIC);
    } finally {
      // Clear on success too
      setIsAddToCartLoading(false);
    }
  };

  const totalItemsCount = itemsToAdd.reduce((sum, i) => sum + i.quantity, 0);
  const buttonText = selectedVariant?.availableForSale
    ? additionalProducts.length > 0 && totalItemsCount > 1
      ? `Add ${totalItemsCount} Items To Cart`
      : 'Add To Cart'
    : 'OUT OF STOCK';

  return (
    <>
      <CTAButton
        data-cy="add-to-cart"
        // Shopify option values. Tests wait on this before reading disabled.
        data-variant={(selectedVariant.selectedOptions ?? []).map((o) => o.value).join('|')}
        onClick={addToCartHandle}
        disabled={isAddToCartLoading || !selectedVariant.availableForSale}
        aria-busy={isAddToCartLoading}
        className={`w-full ${className}`}
        radius={radius}
        icon={
          isAddToCartLoading ? (
            <SpinnerIcon className="h-4 w-4 shrink-0 animate-spin" />
          ) : null
        }
      >
        {buttonText}
      </CTAButton>
      {cartError && (
        <p role="alert" className="mt-2 text-sm text-dark-red">
          {cartError}
        </p>
      )}
    </>
  );
};

export default AddToCartButton;
